'use client'

import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { FIT_ANALYSIS_COPY } from '@/lib/analysis-wait'
import { cn } from '@/lib/utils'

export type AnalysisRetryButtonMode = 'try' | 'run'

export type AnalysisRetryButtonProps = {
  mode: AnalysisRetryButtonMode
  onClick: () => void
  pending?: boolean
  disabled?: boolean
  /** Overrides the default Try again / Re-run analysis accessible name (list actions). */
  ariaLabel?: string
}

/** Try again / Re-run analysis control. Not the recruiter “Run Analysis” CTA. */
export function AnalysisRetryButton({
  mode,
  onClick,
  pending = false,
  disabled = false,
  ariaLabel,
}: AnalysisRetryButtonProps) {
  const label = mode === 'try' ? 'Try again' : 'Re-run analysis'

  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      aria-label={ariaLabel}
      disabled={disabled || pending}
      onClick={(event) => {
        event.stopPropagation()
        onClick()
      }}
    >
      {pending ? <Loader2 className="animate-spin" aria-hidden /> : null}
      {label}
    </Button>
  )
}

export type FitRetryStatusState = 'pending' | 'stuck' | 'missing'

export type FitRetryStatusProps = {
  tone: 'candidate' | 'employer'
  state: FitRetryStatusState
  onRetry?: () => void
  retryPending?: boolean
  showButton?: boolean
  className?: string
}

export function resolveFitRetryState(
  pending: boolean,
  stuck: boolean,
): FitRetryStatusState {
  if (stuck) return 'stuck'
  if (pending) return 'pending'
  return 'missing'
}

export function FitRetryStatus({
  tone,
  state,
  onRetry,
  retryPending = false,
  showButton = true,
  className,
}: FitRetryStatusProps) {
  const copy =
    state === 'pending'
      ? tone === 'candidate'
        ? FIT_ANALYSIS_COPY.candidatePending
        : FIT_ANALYSIS_COPY.employerPending
      : state === 'stuck'
        ? FIT_ANALYSIS_COPY.stuck
        : FIT_ANALYSIS_COPY.missing

  return (
    <div className={cn('flex flex-wrap items-center gap-2', className)}>
      <p
        className="flex items-center gap-2 text-sm text-muted-foreground"
        role="status"
        aria-live="polite"
      >
        {state === 'pending' ? <Loader2 className="h-4 w-4 shrink-0 animate-spin" aria-hidden /> : null}
        <span>{copy}</span>
      </p>
      {showButton && state !== 'pending' && onRetry ? (
        <AnalysisRetryButton mode="try" onClick={onRetry} pending={retryPending} />
      ) : null}
    </div>
  )
}
